import { useCallback, useMemo, useState } from 'react';

import orderBy from 'lodash.orderby';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';

export function useSort<T>(
  data: T[],
  initialSort: string,
  sortableFieldsRecords: Partial<Record<keyof T, { value: string }>>
) {
  const [sort, setSort] = useState<string>(initialSort);

  const handleSort = useCallback(
    (field: keyof T) => {
      if (sort === field) {
        setSort(`-${field.toString()}`); // Desc
      } else if (sort === `-${field.toString()}`) {
        setSort(''); // None
      } else {
        setSort(field.toString()); // Asc
      }
    },
    [sort]
  );

  const getSortIcon = useCallback(
    (field: keyof T) => {
      if (sort === field) {
        return ChevronUp;
      } else if (sort === `-${field.toString()}`) {
        return ChevronDown;
      }
      return ChevronsUpDown;
    },
    [sort]
  );

  const sortedData = useMemo(() => {
    if (!sort) return data;

    const field = sort.startsWith('-') ? sort.slice(1) : sort;
    const key = sortableFieldsRecords[field as keyof T]?.value;
    const direction: 'asc' | 'desc' = sort.startsWith('-') ? 'desc' : 'asc';

    const sorted = orderBy(data, [key], [direction]);
    return sorted;
  }, [data, sort, sortableFieldsRecords]);

  return { sortedData: sortedData as T[], handleSort, currentSort: sort, getSortIcon };
}
